import React from "react";
import { View, TouchableOpacity } from "react-native";
import { Stack } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useUnistyles } from "react-native-unistyles";
import { Icon } from "@src/components/Icon";

export interface StreamingShellProps {
  /** Content to render inside the streaming shell */
  children: React.ReactNode;
  /** Callback when close button is pressed */
  onClose: () => void;
  /** Whether to show the close button */
  showCloseButton?: boolean;
}

/**
 * Shared layout component for fullscreen camera streaming screens.
 *
 * Provides:
 * - Black fullscreen background
 * - Hidden navigation header
 * - Top-left close button with consistent styling
 *
 * @example
 * ```tsx
 * <StreamingShell onClose={() => router.back()}>
 *   <YourStreamingContent />
 * </StreamingShell>
 * ```
 */
export function StreamingShell({ children, onClose, showCloseButton = true }: StreamingShellProps) {
  const insets = useSafeAreaInsets();
  const { theme } = useUnistyles();

  return (
    <View style={{ flex: 1, backgroundColor: "black" }}>
      <Stack.Screen options={{ headerShown: false }} />

      {children}

      {showCloseButton && (
        <TouchableOpacity
          onPress={onClose}
          style={{
            position: "absolute",
            top: insets.top + theme.spacing.md,
            left: Math.max(insets.left, theme.spacing.xl),
            width: theme.spacing["12xl"],
            height: theme.spacing["12xl"],
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <Icon name="closeCircleFilled" size={theme.spacing["11xl"]} color={theme.colors.white.default} />
        </TouchableOpacity>
      )}
    </View>
  );
}
